<template>
  <BaseModal :is-open="isOpen" fullscreen size="lg" @close="emit('close')">
    <template #header>
      <div class="flex justify-between px-4 pt-6 sm:px-8 sm:pt-8">
        <div class="text-2xl font-bold w-3/4">
          {{ $t('tickets.payment_info') }}
        </div>
        <BaseButton
          theme="white"
          size="sm"
          class="w-8 !p-0"
          @click="emit('close')"
        >
          <BaseIcon name="outline_x" />
        </BaseButton>
      </div>
    </template>
    <template #body>
      <div class="px-4 py-4 sm:px-8 sm:pb-8 flex flex-col gap-6">
        <BaseForm :method="submit" class="space-y-4" :reset-on-submit="false">
          <BaseSelectAsync
            v-model="countryId"
            :placeholder="$t('profile.select_country')"
            :label="$t('tickets.card_country')"
            name="country_id"
            required
            :initial="initialCountry"
            :fetch="getCountriesAutocomplete"
          />
          <template v-if="isCompany">
            <BaseInput
              v-model="requisites.company_name"
              name="company_name"
              :label="$t('tickets.company_name')"
              required
            />
            <BaseTextarea
              v-model="requisites.company_address"
              name="company_address"
              :label="$t('tickets.company_address')"
              required
              :placeholder="$t('tickets.company_address_placeholder')"
              :rows="3"
            />
            <BaseInput
              v-model="requisites.company_vat_id"
              name="company_vat_id"
              :label="
                isRu ? $t('tickets.company_inn') : $t('tickets.company_vat_id')
              "
              :placeholder="isRu ? '0123456789' : '12345678U'"
              required
            />
          </template>
          <BaseCheckbox
            v-if="showOffer"
            v-model="acceptOffer"
            background-class="bg-white"
            name="offer"
            rules="required"
          >
            <template #label>
              <i18n-t keypath="tickets.offer_accept_text" tag="div">
                <template #offer>
                  <BaseLink :href="publicOfferUrl" target="_blank"
                    >{{ $t('tickets.offer_accept_text_offer') }}
                  </BaseLink>
                </template>
              </i18n-t>
            </template>
          </BaseCheckbox>
          <div class="flex flex-wrap md:flex-nowrap gap-2 pt-2">
            <BaseButton
              type="submit"
              class="flex-1"
              @click="isRequestInvoice = false"
            >
              {{
                isCompany
                  ? $t('tickets.pay_with_corporate_card')
                  : $t('tickets.pay_by_card')
              }}
            </BaseButton>
            <BaseButton
              v-if="isCompany"
              type="submit"
              look="border"
              theme="primary"
              class="flex-1"
              @click=";(isRequestInvoice = true) && (isCryptoInvoice = false)"
            >
              {{ $t('tickets.pay_by_transfer') }}
            </BaseButton>
            <BaseButton
              v-if="!isCompany"
              type="submit"
              look="border"
              theme="primary"
              class="flex-1"
              @click=";(isRequestInvoice = true) && (isCryptoInvoice = true)"
            >
              {{ $t('tickets.pay_by_crypto') }}
            </BaseButton>
          </div>
        </BaseForm>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import type { CountryInterface } from '~/models/geo'
import { EventType } from '~/models/events'
import type { CompanyRequisites } from '~/models/tickets'

interface PropsInterface {
  isOpen?: boolean
  country: CountryInterface | null
  showOffer?: boolean
  eventType?: EventType
  eventSlug?: string
  isCompany?: boolean
}

const props = withDefaults(defineProps<PropsInterface>(), {
  isOpen: false,
  country: null,
  showOffer: false,
  eventType: EventType.Default,
  eventSlug: '',
  isCompany: false,
})

const acceptOffer = ref(false)
const isRequestInvoice = ref(false)
const isCryptoInvoice = ref(false)
const requisites = reactive<CompanyRequisites>({
  company_name: '',
  company_address: '',
  company_vat_id: '',
  company_country: '',
})

const clearRequisites = () => {
  requisites.company_name = ''
  requisites.company_address = ''
  requisites.company_vat_id = ''
  requisites.company_country = ''
}

const emit = defineEmits(['close', 'pay'])

const { getCountriesAutocomplete } = useGeo()

const countryId = ref<string | undefined>(props.country?.id)
const isRu = computed(() => countryId.value === 'RU')

const initialCountry = computed(() => {
  return props.country && typeof props.country === 'object'
    ? toAutocomplete(props.country)
    : undefined
})

const resetCountryId = () => {
  countryId.value = props.country?.id
}

const publicOfferUrl = computed(() => {
  const route =
    props.eventType === EventType.Default
      ? `/event-info/${props.eventSlug}/public-offer`
      : '/academy/public-offer'

  return useLocaleRoute()(route)?.fullPath || ''
})

const closeForm = () => {
  emit('close')
  clearRequisites()
  acceptOffer.value = false
  resetCountryId()
}

const submit = () => {
  if (props.isCompany && countryId.value) {
    requisites.company_country = countryId.value
  }

  emit(
    'pay',
    isRequestInvoice.value,
    isCryptoInvoice.value,
    countryId.value,
    false,
    props.isCompany ? { ...requisites } : undefined
  )
  closeForm()
}
</script>
